import { heightSinusoid } from "@nav-games/tides";

interface TidalCurveProps {
  hwHeight: number;
  lwHeight: number;
  /// Total half-cycle duration in hours (HW to LW).
  cycleDurationHours: number;
  /// Optional query — hours from HW (positive = ebbing). Renders a
  /// dot on the curve at this time with the height labelled.
  queryHoursFromHw?: number;
  size?: number;
}

const PAD_LEFT = 36;
const PAD_RIGHT = 12;
const PAD_TOP = 12;
const PAD_BOTTOM = 24;
const WIDTH = 360;
const HEIGHT = 180;

/// Cosine-curve view of a single HW → LW half-cycle. Time on the
/// x-axis (0 at HW, cycleDurationHours at LW); height on the
/// y-axis (HW height at top, LW height at bottom). When the caller
/// passes `queryHoursFromHw`, a marker dot + dashed crosshairs
/// indicate the predicted height at that moment.
export function TidalCurve({
  hwHeight,
  lwHeight,
  cycleDurationHours,
  queryHoursFromHw,
  size = WIDTH,
}: TidalCurveProps) {
  const xs = WIDTH - PAD_LEFT - PAD_RIGHT;
  const ys = HEIGHT - PAD_TOP - PAD_BOTTOM;
  const yForHeight = (h: number) => {
    if (hwHeight === lwHeight) return PAD_TOP + ys / 2;
    return (
      PAD_TOP + ((hwHeight - h) / (hwHeight - lwHeight)) * ys
    );
  };
  const xForHours = (h: number) =>
    PAD_LEFT + (h / cycleDurationHours) * xs;
  // Sample 60 points along the curve for a smooth line.
  const points: string[] = [];
  for (let i = 0; i <= 60; i++) {
    const t = (i / 60) * cycleDurationHours;
    const h = heightSinusoid(hwHeight, lwHeight, t, cycleDurationHours);
    points.push(`${xForHours(t).toFixed(2)},${yForHeight(h).toFixed(2)}`);
  }
  const hwY = yForHeight(hwHeight);
  const lwY = yForHeight(lwHeight);
  const queryHasResult =
    queryHoursFromHw !== undefined &&
    queryHoursFromHw >= 0 &&
    queryHoursFromHw <= cycleDurationHours;
  const queryHeight = queryHasResult
    ? heightSinusoid(
        hwHeight,
        lwHeight,
        queryHoursFromHw,
        cycleDurationHours,
      )
    : null;
  const queryX = queryHasResult ? xForHours(queryHoursFromHw!) : 0;
  const queryY = queryHasResult ? yForHeight(queryHeight!) : 0;

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      width={size}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
      role="img"
      aria-label={
        queryHasResult
          ? `Tidal curve — height ${queryHeight!.toFixed(2)} m at ${queryHoursFromHw!.toFixed(2)} hours from HW`
          : "Tidal curve between HW and LW"
      }
    >
      {/* HW + LW grid lines */}
      <line
        x1={PAD_LEFT}
        y1={hwY}
        x2={WIDTH - PAD_RIGHT}
        y2={hwY}
        stroke="#cbd6df"
        strokeDasharray="3 3"
      />
      <line
        x1={PAD_LEFT}
        y1={lwY}
        x2={WIDTH - PAD_RIGHT}
        y2={lwY}
        stroke="#cbd6df"
        strokeDasharray="3 3"
      />
      <text
        x={PAD_LEFT - 4}
        y={hwY + 3}
        textAnchor="end"
        fontSize="10"
        fill="#5d6878"
        fontFamily="ui-sans-serif, system-ui"
      >
        HW {hwHeight.toFixed(1)} m
      </text>
      <text
        x={PAD_LEFT - 4}
        y={lwY + 3}
        textAnchor="end"
        fontSize="10"
        fill="#5d6878"
        fontFamily="ui-sans-serif, system-ui"
      >
        LW {lwHeight.toFixed(1)} m
      </text>

      {/* Hour-tick marks at integer hours */}
      <g
        stroke="#cbd6df"
        fontSize="9"
        fontFamily="ui-sans-serif, system-ui"
        fill="#5d6878"
      >
        {Array.from({ length: Math.floor(cycleDurationHours) + 1 }, (_, i) => {
          const x = xForHours(i);
          return (
            <g key={i}>
              <line
                x1={x}
                y1={HEIGHT - PAD_BOTTOM}
                x2={x}
                y2={HEIGHT - PAD_BOTTOM + 4}
              />
              <text
                x={x}
                y={HEIGHT - PAD_BOTTOM + 14}
                textAnchor="middle"
                stroke="none"
              >
                {i}
              </text>
            </g>
          );
        })}
        <text
          x={(PAD_LEFT + WIDTH - PAD_RIGHT) / 2}
          y={HEIGHT - 4}
          textAnchor="middle"
          stroke="none"
        >
          Hours from HW
        </text>
      </g>

      {/* Curve */}
      <polyline
        points={points.join(" ")}
        fill="none"
        stroke="#1e6091"
        strokeWidth="2"
      />

      {/* Query marker */}
      {queryHasResult && (
        <g>
          <line
            x1={queryX}
            y1={PAD_TOP}
            x2={queryX}
            y2={HEIGHT - PAD_BOTTOM}
            stroke="#c0382b"
            strokeDasharray="2 3"
            strokeWidth="0.8"
          />
          <line
            x1={PAD_LEFT}
            y1={queryY}
            x2={queryX}
            y2={queryY}
            stroke="#c0382b"
            strokeDasharray="2 3"
            strokeWidth="0.8"
          />
          <circle cx={queryX} cy={queryY} r="4" fill="#c0382b" />
          <text
            x={queryX + 8}
            y={queryY - 6}
            fontSize="11"
            fill="#c0382b"
            fontWeight="600"
            fontFamily="ui-sans-serif, system-ui"
          >
            {queryHeight!.toFixed(2)} m
          </text>
        </g>
      )}
    </svg>
  );
}
